import { API_BASE_URL, apiRequest, getCurrentAccessToken } from '@/shared/lib/api-client';

export interface SearchTicket {
  id: number;
  protocol_number: string | null;
  status: string;
  contact_id: number;
  contact_name: string | null;
  contact_phone: string | null;
  whatsapp_number_id: number;
  whatsapp_display_number: string | null;
  agent_id: number | null;
  agent_name: string | null;
  department_id: number | null;
  department_name: string | null;
  closing_reason_id: number | null;
  closing_reason: string | null;
  /** Nota da Pesquisa de Satisfação (1-5). null = não avaliado. */
  rating: number | null;
  notes: string | null;
  created_at: string;
  last_message_at: string;
  closed_at: string | null;
  tags: Array<{ id: number; name: string; color: string }>;
}

export interface SearchFilters {
  date_range?: { start?: string | undefined; end?: string | undefined } | undefined;
  date_field?: 'created_at' | 'closed_at' | 'last_message_at' | undefined;
  name?: string | undefined;
  contact_phone?: string | undefined;
  status?: Array<'active' | 'pending' | 'closed'> | undefined;
  whatsapp_number_ids?: number[] | undefined;
  agent_ids?: number[] | undefined;
  department_ids?: number[] | undefined;
  tag_ids?: number[] | undefined;
  closing_reason_ids?: number[] | undefined;
  /** Notas da Pesquisa de Satisfação (1-5). */
  ratings?: number[] | undefined;
  has_notes?: boolean | undefined;
  protocol_number?: string | undefined;
  actions?: Array<'created' | 'transferred' | 'reopened' | 'closed' | 'queued'> | undefined;
  limit?: number | undefined;
  cursor?: string | undefined;
}

export const searchTickets = (
  filters: SearchFilters,
): Promise<{ tickets: SearchTicket[]; next_cursor: string | null }> =>
  apiRequest('/consultation/search', { method: 'POST', body: filters });

/** Item do timeline de auditoria do ticket. `id` é `number` pra ticket_events
 *  e `string` pra eventos virtuais (`"chatbot:N:start" | "chatbot:N:end"`). */
export interface TimelineItem {
  id: number | string;
  event_type: string;
  actor_user_id: number | null;
  actor_name: string | null;
  payload: unknown;
  created_at: string;
}

export const getTimeline = (ticketId: number): Promise<TimelineItem[]> =>
  apiRequest(`/consultation/tickets/${ticketId}/timeline`);

export interface PresetRow {
  slug: string;
  name: string;
  filters: SearchFilters;
}
export const listPresets = (): Promise<PresetRow[]> => apiRequest('/consultation/presets');
export const savePreset = (input: { name: string; filters: SearchFilters }): Promise<{
  slug: string;
}> => apiRequest('/consultation/presets', { method: 'POST', body: input });
export const deletePreset = (slug: string): Promise<void> =>
  apiRequest(`/consultation/presets/${slug}`, { method: 'DELETE' });

export async function exportConsultation(
  filters: SearchFilters,
  format: 'csv' | 'xlsx',
): Promise<void> {
  const token = getCurrentAccessToken();
  const res = await fetch(`${API_BASE_URL}/consultation/export`, {
    method: 'POST',
    body: JSON.stringify({ ...filters, format }),
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { authorization: `Bearer ${token}` }),
    },
  });
  if (!res.ok) {
    // O backend responde Problem Details (JSON). Extrai a mensagem legível em vez
    // de jogar o JSON cru no toast.
    let msg = `Falha ao exportar (${res.status})`;
    try {
      const p = JSON.parse(await res.text()) as { title?: string; detail?: string };
      msg = p.detail || p.title || msg;
    } catch {
      /* resposta não-JSON — mantém a mensagem padrão */
    }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `consulta.${format}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
